import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Slider } from './ui/slider';
import { MapPin, Star, DollarSign, Navigation, Loader2, ExternalLink, Clock, AlertTriangle } from 'lucide-react';
import { db } from '../db';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AISuggestionsModal = ({ open, onClose, type, dailyBudget, location }) => {
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedRadius, setSelectedRadius] = useState(5);
  const [isCached, setIsCached] = useState(false);
  const [budgetContext, setBudgetContext] = useState(null);

  // Load full budget context when modal opens
  useEffect(() => {
    if (open) {
      loadBudgetContext();
    }
  }, [open]);

  const loadBudgetContext = async () => {
    try {
      // Get trip settings
      const totalBudgetSetting = await db.settings.get('total_budget');
      const tripDurationSetting = await db.settings.get('trip_duration');
      const tripStartSetting = await db.settings.get('trip_start_date');
      const currencySetting = await db.settings.get('currency');
      const currentTripIdSetting = await db.settings.get('current_trip_id');
      
      const totalBudget = parseFloat(totalBudgetSetting?.value || 0);
      const tripDuration = parseInt(tripDurationSetting?.value || 30);
      const currency = currencySetting?.value || 'USD';
      const currentTripId = currentTripIdSetting?.value || null;
      
      // Calculate days elapsed
      const startDate = tripStartSetting?.value ? new Date(tripStartSetting.value) : new Date();
      const today = new Date();
      const daysElapsed = Math.max(1, Math.ceil((today - startDate) / (1000 * 60 * 60 * 24)));
      const daysRemaining = Math.max(1, tripDuration - daysElapsed);
      
      // Get total spent for current trip
      const allExpenses = await db.expenses
        .filter(expense => !expense.deleted && !expense.archived && 
          (!expense.trip_id || expense.trip_id === currentTripId))
        .toArray();
      
      const totalSpent = allExpenses.reduce((sum, exp) => sum + exp.amount, 0);
      const remainingBudget = totalBudget - totalSpent;
      const dailyBurnRate = totalSpent / daysElapsed;
      const recommendedDailyBudget = remainingBudget / daysRemaining;
      
      // Determine budget status
      let budgetStatus = 'on-track';
      if (dailyBurnRate > (totalBudget / tripDuration) * 1.2) {
        budgetStatus = 'overspending';
      } else if (dailyBurnRate < (totalBudget / tripDuration) * 0.8) {
        budgetStatus = 'underspending';
      }
      
      setBudgetContext({
        totalBudget,
        remainingBudget,
        tripDuration,
        daysElapsed,
        daysRemaining,
        totalSpent,
        dailyBurnRate,
        recommendedDailyBudget,
        budgetStatus,
        currency
      });
    } catch (error) {
      console.error('Failed to load budget context:', error);
    }
  };

  const typeConfig = {
    food: {
      title: 'Food Recommendations',
      icon: '🍽️',
      emoji: '🍕',
      budgetPercent: 0.3,
      placeType: 'restaurant'
    },
    lodging: {
      title: 'Lodging Options',
      icon: '🏨',
      emoji: '🛏️',
      budgetPercent: 0.5,
      placeType: 'lodging'
    },
    transport: {
      title: 'Transport Options',
      icon: '🚗',
      emoji: '🚕',
      budgetPercent: 0.2,
      placeType: 'transit_station'
    }
  };

  const config = typeConfig[type] || typeConfig.food;
  
  // Calculate budget for this type based on REMAINING budget context
  const getSmartBudgetForType = () => {
    if (!budgetContext) {
      return dailyBudget * config.budgetPercent;
    }
    
    // Use recommended daily budget based on remaining funds, not original daily budget
    const smartDailyBudget = budgetContext.recommendedDailyBudget;
    return smartDailyBudget * config.budgetPercent;
  };
  
  const budgetForType = getSmartBudgetForType();

  // Format distance display (show decimal for < 1 mile)
  const formatDistance = (miles) => {
    if (miles < 1) {
      return `${miles.toFixed(1)} mi`;
    }
    return `${Math.round(miles)} mi`;
  };

  const handleSearch = async () => {
    setLoading(true);
    setShowResults(true);

    try {
      // Pass full budget context to backend for smarter suggestions
      const response = await axios.post(`${API}/ai/suggestions`, {
        type: type,
        budget: budgetForType,
        location: location,
        radius_miles: selectedRadius,
        daily_budget: dailyBudget,
        // Full budget context for smarter recommendations
        budget_context: budgetContext ? {
          total_budget: budgetContext.totalBudget,
          remaining_budget: budgetContext.remainingBudget,
          days_remaining: budgetContext.daysRemaining,
          daily_burn_rate: budgetContext.dailyBurnRate,
          recommended_daily_budget: budgetContext.recommendedDailyBudget,
          budget_status: budgetContext.budgetStatus,
          currency: budgetContext.currency
        } : null
      });

      setSuggestions(response.data.suggestions || []);
      setIsCached(response.data.cached || false);
      
      if (response.data.suggestions?.length === 0) {
        toast.info('No places found in this radius. Try a larger area.');
      }
    } catch (error) {
      console.error('Failed to get suggestions:', error);
      toast.error('Failed to load suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowResults(false);
    setSuggestions([]);
    setSelectedRadius(5);
    setIsCached(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="ai-suggestions-modal" className="bg-card border-border max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading flex items-center gap-2">
            <span className="text-3xl">{config.icon}</span>
            {config.title}
          </DialogTitle>
          
          {/* Budget Context Display */}
          {budgetContext && (
            <div className="mt-3 space-y-2">
              {/* Budget status warning if overspending */}
              {budgetContext.budgetStatus === 'overspending' && (
                <div className="flex items-center gap-2 text-xs text-amber-500 bg-amber-500/10 px-3 py-2 rounded-lg">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>You're spending faster than planned. Showing budget-friendly options.</span>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-secondary/50 rounded-lg px-3 py-2">
                  <p className="text-muted-foreground">Remaining Budget</p>
                  <p className="font-bold text-foreground">
                    {budgetContext.currency} {budgetContext.remainingBudget.toFixed(0)}
                  </p>
                </div>
                <div className="bg-secondary/50 rounded-lg px-3 py-2">
                  <p className="text-muted-foreground">Days Left</p>
                  <p className="font-bold text-foreground">{budgetContext.daysRemaining} days</p>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Recommended for {type}: <span className="text-primary font-semibold">${budgetForType.toFixed(0)}/day</span>
              </p>
            </div>
          )}
          
          {!budgetContext && (
            <p className="text-sm text-muted-foreground mt-2">
              Within your budget: ${budgetForType.toFixed(0)} per day
            </p>
          )}
        </DialogHeader>

        {/* Radius Selection with Slider */}
        {!showResults && (
          <div className="space-y-6 py-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Search radius
                </p>
                <div className="flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-primary" />
                  <span className="text-xl font-bold text-primary min-w-[70px] text-right">
                    {formatDistance(selectedRadius)}
                  </span>
                </div>
              </div>
              
              {/* Slider */}
              <div className="px-2">
                <Slider
                  data-testid="radius-slider"
                  value={[selectedRadius]}
                  onValueChange={(value) => setSelectedRadius(value[0])}
                  min={0.25}
                  max={100}
                  step={0.25}
                  className="w-full"
                />
              </div>
              
              {/* Min/Max labels */}
              <div className="flex justify-between text-xs text-muted-foreground px-2">
                <span>0.25 mi</span>
                <span>100 mi</span>
              </div>
              
              {/* Quick select buttons */}
              <div className="flex gap-2 justify-center flex-wrap">
                {[0.5, 1, 5, 10, 25, 50].map((miles) => (
                  <Button
                    key={miles}
                    variant={selectedRadius === miles ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedRadius(miles)}
                    className="text-xs h-7 px-3"
                  >
                    {miles < 1 ? `${miles} mi` : `${miles} mi`}
                  </Button>
                ))}
              </div>
            </div>

            {/* Search Button */}
            <Button
              data-testid="search-suggestions-button"
              onClick={handleSearch}
              className="w-full h-12 rounded-full bg-primary text-primary-foreground font-semibold"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Find {config.title.replace(' Options', '').replace(' Recommendations', '')}
            </Button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Finding top-rated options within {formatDistance(selectedRadius)}...</p>
          </div>
        )}

        {/* Results */}
        {!loading && showResults && suggestions.length > 0 && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Top 3 highest rated within {formatDistance(selectedRadius)}
                </p>
                {isCached && (
                  <p className="text-xs text-primary/70 flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    Cached results (updated every 24h)
                  </p>
                )}
              </div>
              <Button
                onClick={() => {
                  setShowResults(false);
                  setSuggestions([]);
                }}
                variant="ghost"
                size="sm"
                className="text-xs"
              >
                Change radius
              </Button>
            </div>

            {suggestions.map((place, index) => (
              <Card
                key={index}
                data-testid={`suggestion-${index}`}
                className="bg-secondary/50 border-border hover:border-primary transition-colors"
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground text-lg">
                        {place.name}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {place.address}
                      </p>
                    </div>
                    {place.rating && (
                      <div className="flex items-center gap-1 bg-primary/20 px-2 py-1 rounded-full">
                        <Star className="w-3 h-3 text-primary fill-primary" />
                        <span className="text-sm font-semibold text-primary">
                          {place.rating}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm">
                    {place.price_level && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <DollarSign className="w-4 h-4" />
                        <span>{'$'.repeat(place.price_level)}</span>
                      </div>
                    )}
                    {place.distance && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        <span>{place.distance}</span>
                      </div>
                    )}
                  </div>

                  {place.why_recommended && (
                    <p className="text-sm text-muted-foreground italic border-l-2 border-primary pl-3">
                      {place.why_recommended}
                    </p>
                  )}

                  {place.url && (
                    <Button
                      onClick={() => window.open(place.url, '_blank')}
                      variant="outline"
                      size="sm"
                      className="w-full rounded-full border-primary text-primary hover:bg-primary/10"
                    >
                      <ExternalLink className="w-3 h-3 mr-2" />
                      View Details
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Results */}
        {!loading && showResults && suggestions.length === 0 && (
          <div className="py-8 text-center space-y-3">
            <p className="text-muted-foreground">
              No {type} options found within {formatDistance(selectedRadius)}
            </p>
            <Button
              onClick={() => {
                setShowResults(false);
                setSuggestions([]);
              }}
              variant="outline"
              className="rounded-full"
            >
              Try Different Radius
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AISuggestionsModal;
